import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import type { ChannelSummary } from '../../api/types'
import { ApiError } from '../../api/client'
import { Icon } from '../../components/Icon'
import { db } from '../../db/db'
import { isAdminRole, useSession } from '../../state/session'
import { useConnection } from '../../state/connection'
import { syncAll } from '../../sync/syncEngine'
import { ScreenHeader } from '../shell/ScreenHeader'
import { AdminAccessDialog } from './AdminAccessDialog'
import { openAdminChannel, releaseChannelView, retainChannelView } from './adminView'
import { ConversationPane } from './ConversationPane'

type LoadState = 'loading' | 'missing' | 'reason-required'

export function ConversationRoute() {
  const { channelId = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const me = useSession((s) => s.me)
  const online = useConnection((s) => s.online)
  const channel = useLiveQuery(() => db.channels.get(channelId), [channelId], null)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  // Bumped when a reason has been given, to open the conversation again.
  const [attempt, setAttempt] = useState(0)
  const adminSummary = (location.state as { adminSummary?: ChannelSummary } | null)?.adminSummary
  const isAdmin = isAdminRole(me)
  const known = Boolean(channel)

  // Unknown locally: admins load it as a non-member view; others sync once (e.g. just added).
  useEffect(() => {
    if (known || channel === null) return
    let cancelled = false
    setLoadState('loading')
    const task = isAdmin ? openAdminChannel(channelId) : syncAll().then(() => db.channels.get(channelId).then(Boolean))
    task
      .then((found) => {
        if (!cancelled && !found) setLoadState('missing')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Not a dead end: the conversation is someone else's and the app has not said why it is being opened.
        setLoadState(error instanceof ApiError && error.error === 'reason_required' ? 'reason-required' : 'missing')
      })
    return () => {
      cancelled = true
    }
  }, [channelId, known, channel, isAdmin, online, attempt])

  // Drop a non-member admin view when navigating away.
  useEffect(() => {
    if (!isAdmin) return
    retainChannelView(channelId)
    return () => releaseChannelView(channelId)
  }, [channelId, isAdmin])

  if (channel && me) return <ConversationPane key={channel.id} channel={channel} me={me} />

  if (loadState === 'reason-required') {
    return (
      <AdminAccessDialog
        channelId={channelId}
        name={adminSummary?.name ?? 'this conversation'}
        onOpened={() => {
          setLoadState('loading')
          setAttempt((n) => n + 1)
        }}
        onCancel={() => navigate('/')}
      />
    )
  }

  return (
    <div className="screen">
      <ScreenHeader title={loadState === 'missing' ? 'Conversation unavailable' : 'Loading…'} />
      <div className="chat-empty">
        {loadState === 'missing' ? (
          <>
            <Icon name="alert" />
            <h2>{online ? "This conversation isn't available" : "You're offline"}</h2>
            <p>{online ? 'It may have been removed, or you are no longer a member.' : 'This conversation is not stored on this device yet.'}</p>
          </>
        ) : (
          <span className="spinner" aria-label="Loading conversation" />
        )}
      </div>
    </div>
  )
}
